import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Spin, Tag, Space, Modal, Row, Col } from 'antd';
import { get, put, post, getMerchantApps, getCurrentAppId } from '../../utils/merchantApi';
import CryptoJS from 'crypto-js';

const MerchantSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [appInfo, setAppInfo] = useState(null);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [secretModalVisible, setSecretModalVisible] = useState(false);
  const [appSecret, setAppSecret] = useState('');
  const [fetchingSecret, setFetchingSecret] = useState(false);
  const [gaEnabled, setGaEnabled] = useState(false);
  const [gaLoading, setGaLoading] = useState(false);
  const [gaModalVisible, setGaModalVisible] = useState(false);
  const [gaInitData, setGaInitData] = useState(null);
  const [gaCode, setGaCode] = useState('');
  const [gaSubmitting, setGaSubmitting] = useState(false);
  const [gaDisableModalVisible, setGaDisableModalVisible] = useState(false);
  const [gaDisableCode, setGaDisableCode] = useState('');

  useEffect(() => {
    fetchAppInfo();
    fetchGaStatus();
  }, []);

  const fetchGaStatus = async () => {
    try {
      const response = await get('/app/auth/ga/status');
      setGaEnabled(response.data?.gaEnabled === true);
    } catch (error) {
      console.error('获取Google验证器状态失败:', error);
    }
  };

  const fetchAppInfo = async () => {
    setLoading(true);
    try {
      const apps = getMerchantApps();
      const currentAppId = getCurrentAppId();
      
      if (!currentAppId) {
        message.error('请先选择应用');
        return;
      }

      const response = await get('/merchant/info');
      setAppInfo(response.data);
      form.setFieldsValue({
        app_name: response.data.app_name,
        app_desc: response.data.app_desc,
        callback_url: response.data.callback_url,
        webhook_url: response.data.webhook_url,
        ip_whitelist: response.data.ip_whitelist
      });
    } catch (error) {
      console.error('获取应用信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      await put('/merchant/info', {
        callback_url: values.callback_url,
        webhook_url: values.webhook_url,
        ip_whitelist: values.ip_whitelist
      });
      message.success('保存成功');
    } catch (error) {
      console.error('保存失败:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (values) => {
    try {
      const oldPassword = CryptoJS.SHA256(values.oldPassword).toString();
      const newPassword = CryptoJS.SHA256(values.newPassword).toString();
      
      await post('/merchant/password', {
        oldPassword,
        newPassword
      });
      
      message.success('密码修改成功');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      console.error('修改密码失败:', error);
    }
  };

  const handleViewSecret = async () => {
    setFetchingSecret(true);
    try {
      const response = await get('/merchant/secret');
      setAppSecret(response.data.app_secret);
      setSecretModalVisible(true);
    } catch (error) {
      console.error('获取密钥失败:', error);
    } finally {
      setFetchingSecret(false);
    }
  };

  const handleGaInit = async () => {
    setGaLoading(true);
    try {
      const response = await post('/app/auth/ga/init');
      setGaInitData(response.data);
      setGaCode('');
      setGaModalVisible(true);
    } catch (error) {
      console.error('生成Google验证器失败:', error);
    } finally {
      setGaLoading(false);
    }
  };

  const handleGaVerify = async () => {
    if (!/^\d{6}$/.test(gaCode)) {
      message.warning('请输入6位动态验证码');
      return;
    }
    setGaSubmitting(true);
    try {
      await post('/app/auth/ga/verify', { secret: gaInitData.secret, gaCode });
      message.success('Google验证器已启用');
      setGaModalVisible(false);
      setGaInitData(null);
      setGaCode('');
      setGaEnabled(true);
    } catch (error) {
      console.error('启用Google验证器失败:', error);
    } finally {
      setGaSubmitting(false);
    }
  };

  const handleGaDisable = async () => {
    if (!/^\d{6}$/.test(gaDisableCode)) {
      message.warning('请输入6位动态验证码');
      return;
    }
    setGaSubmitting(true);
    try {
      await post('/app/auth/ga/disable', { gaCode: gaDisableCode });
      message.success('Google验证器已关闭');
      setGaDisableModalVisible(false);
      setGaDisableCode('');
      setGaEnabled(false);
    } catch (error) {
      console.error('关闭Google验证器失败:', error);
    } finally {
      setGaSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {appInfo && (
        <Card title="应用信息" style={{ marginBottom: 16 }}>
          <Space size="large" wrap>
            <div>
              <span style={{ color: '#666' }}>应用ID：</span>
              <span style={{ fontFamily: 'monospace' }}>{appInfo.app_id}</span>
            </div>
            <div>
              <span style={{ color: '#666' }}>状态：</span>
              <Tag color={appInfo.status === 1 ? 'green' : 'red'}>
                {appInfo.status === 1 ? '启用' : '禁用'}
              </Tag>
            </div>
            <div>
              <span style={{ color: '#666' }}>日限额：</span>
              <span>¥{Number(appInfo.daily_limit || 0).toLocaleString()}</span>
            </div>
            <div>
              <span style={{ color: '#666' }}>单笔限额：</span>
              <span>¥{appInfo.single_limit_min} - ¥{appInfo.single_limit_max}</span>
            </div>
            <div>
              <Button type="link" onClick={handleViewSecret} loading={fetchingSecret}>
                查看密钥
              </Button>
            </div>
          </Space>
        </Card>
      )}

      <Card title="回调配置">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item name="app_name" label="应用名称">
            <Input disabled />
          </Form.Item>

          <Form.Item name="app_desc" label="应用描述">
            <Input.TextArea rows={2} disabled />
          </Form.Item>

          <Form.Item 
            name="callback_url" 
            label="支付回调地址"
            extra="用户支付完成后跳转的页面"
          >
            <Input placeholder="例如：https://example.com/payment/callback" />
          </Form.Item>

          <Form.Item 
            name="webhook_url" 
            label="Webhook通知地址"
            extra="支付成功后异步通知的地址"
          >
            <Input placeholder="例如：https://example.com/payment/notify" />
          </Form.Item>

          <Form.Item 
            name="ip_whitelist" 
            label="IP白名单"
            extra="多个IP用逗号分隔，留空表示不限制"
          >
            <Input placeholder="例如：192.168.1.1,10.0.0.0/8" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              保存配置
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="账户安全" style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          <Col>
            <Button type="primary" onClick={() => setPasswordModalVisible(true)}>
              修改登录密码
            </Button>
          </Col>
          <Col>
            <Space>
              <span style={{ color: '#666' }}>Google验证器：</span>
              <Tag color={gaEnabled ? 'green' : 'default'}>
                {gaEnabled ? '已启用' : '未启用'}
              </Tag>
              {gaEnabled ? (
                <Button onClick={() => setGaDisableModalVisible(true)}>关闭</Button>
              ) : (
                <Button onClick={handleGaInit} loading={gaLoading}>开启</Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      <Modal
        title="查看密钥"
        open={secretModalVisible}
        onCancel={() => {
          setSecretModalVisible(false);
          setAppSecret('');
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => {
            setSecretModalVisible(false);
            setAppSecret('');
          }}>
            我知道了
          </Button>
        ]}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ marginBottom: 10, color: '#ff4d4f', fontWeight: 500 }}>
            请妥善保存 App Secret ！
          </p>
          <div style={{ 
            background: '#f5f5f5', 
            padding: '15px', 
            borderRadius: 4,
            wordBreak: 'break-all',
            fontFamily: 'monospace',
            fontSize: 14
          }}>
            {appSecret}
          </div>
        </div>
      </Modal>

      <Modal
        title="修改登录密码"
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordChange}
        >
          <Form.Item
            name="oldPassword"
            label="旧密码"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password placeholder="请输入旧密码" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '新密码至少6位' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请确认新密码" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              确认修改
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="开启Google验证器"
        open={gaModalVisible}
        onCancel={() => {
          setGaModalVisible(false);
          setGaInitData(null);
          setGaCode('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setGaModalVisible(false);
            setGaInitData(null);
            setGaCode('');
          }}>
            取消
          </Button>,
          <Button key="confirm" type="primary" loading={gaSubmitting} onClick={handleGaVerify}>
            确认启用
          </Button>
        ]}
      >
        {gaInitData && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p style={{ marginBottom: 12, color: '#666' }}>
              请使用 Google Authenticator 扫码，或手动输入密钥
            </p>
            <img
              src={gaInitData.qrDataUrl}
              alt="Google验证器二维码"
              style={{ width: 240, height: 240, marginBottom: 16 }}
            />
            <div style={{
              background: '#f5f5f5',
              padding: '10px',
              borderRadius: 4,
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              marginBottom: 16
            }}>
              {gaInitData.secret}
            </div>
            <Input
              placeholder="输入6位动态验证码"
              maxLength={6}
              value={gaCode}
              onChange={(e) => setGaCode(e.target.value.replace(/\D/g, ''))}
              style={{ maxWidth: 220 }}
            />
          </div>
        )}
      </Modal>

      <Modal
        title="关闭Google验证器"
        open={gaDisableModalVisible}
        onCancel={() => {
          setGaDisableModalVisible(false);
          setGaDisableCode('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setGaDisableModalVisible(false);
            setGaDisableCode('');
          }}>
            取消
          </Button>,
          <Button key="confirm" type="primary" danger loading={gaSubmitting} onClick={handleGaDisable}>
            确认关闭
          </Button>
        ]}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: 12, color: '#666' }}>
            请输入当前 Google Authenticator 动态验证码以确认操作
          </p>
          <Input
            placeholder="输入6位动态验证码"
            maxLength={6}
            value={gaDisableCode}
            onChange={(e) => setGaDisableCode(e.target.value.replace(/\D/g, ''))}
            style={{ maxWidth: 220 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default MerchantSettings;
